import React from 'react';
import { Tooltip } from 'antd';
import { colors } from '@/shared/tokens/colors';

/**
 * Small inline badge that displays whether the task is deployed (or has a
 * deploy/undeploy in progress). Returns null when there is nothing to show.
 *
 * When `badge.breakdown` is provided (composed task with mixed deployed
 * versions, or some sub-tasks deployed and others not), a hover tooltip
 * surfaces a per-sub-task version breakdown — the inline label stays in
 * the same green-dot pill style so the card/list visuals are unchanged.
 */
export const TaskDeploymentBadge = ({ badge }) => {
    if (!badge?.label) return null;

    const inline = (
        <div
            className="fs-unmask"
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                color: '#1A1A1A',
                fontSize: 14,
                whiteSpace: 'nowrap',
                flexShrink: 0,
                cursor: badge.breakdown ? 'help' : 'inherit',
            }}
        >
            <span
                style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: badge.color,
                    display: 'inline-block',
                }}
            />
            {badge.label}
        </div>
    );

    if (!badge.breakdown || badge.breakdown.length === 0) return inline;

    return (
        <Tooltip
            placement="topRight"
            title={<DeploymentBreakdown items={badge.breakdown} />}
        >
            {inline}
        </Tooltip>
    );
};

const DeploymentBreakdown = ({ items }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '2px 0' }}>
        <div style={{ fontWeight: 600, marginBottom: 2 }}>Deployment by sub-task</div>
        {items.map((item) => (
            <div
                key={item.name}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
                <span
                    style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background: item.deployed ? colors.green : colors.gray,
                        display: 'inline-block',
                        flexShrink: 0,
                    }}
                />
                <span style={{ fontWeight: 500 }}>{item.name}</span>
                <span style={{ opacity: 0.85 }}>— {item.versionLabel}</span>
            </div>
        ))}
    </div>
);

export default TaskDeploymentBadge;
